'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Base = 2 | 8 | 10 | 16;
const NAMES: Record<Base, string> = { 2: 'Binary', 8: 'Octal', 10: 'Decimal', 16: 'Hex' };
const DIGITS: Record<Base, RegExp> = {
  2: /^[01]+$/,
  8: /^[0-7]+$/,
  10: /^[0-9]+$/,
  16: /^[0-9a-f]+$/i,
};
const PREFIX: Record<Base, string> = { 2: '0b', 8: '0o', 10: '', 16: '0x' };

const OPS = ['+', '−', '×', '÷', 'mod', 'AND', 'OR', 'XOR', '<<', '>>'] as const;
type Op = (typeof OPS)[number];

/** Parse a whole number in a base, allowing a sign, spaces, underscores and a 0b/0o/0x prefix. */
export function parseBase(raw: string, base: Base): bigint | null {
  let s = raw.trim().replace(/[\s_]/g, '');
  let neg = false;
  if (s.startsWith('-')) {
    neg = true;
    s = s.slice(1);
  }
  s = s.replace(/^0[box]/i, '');
  if (!s || !DIGITS[base].test(s)) return null;
  let v = BigInt(0);
  const b = BigInt(base);
  for (const ch of s.toLowerCase()) v = v * b + BigInt(parseInt(ch, 16));
  return neg ? -v : v;
}

/** Binary in groups of 4 bits, hex in bytes (pairs), both padded on the left. */
export function formatBase(v: bigint, base: Base, group = true): string {
  const neg = v < BigInt(0);
  let s = (neg ? -v : v).toString(base).toUpperCase();
  const size = base === 2 ? 4 : base === 16 ? 2 : 0;
  if (group && size) {
    s = s
      .padStart(Math.ceil(s.length / size) * size, '0')
      .replace(new RegExp(`(.{${size}})(?!$)`, 'g'), '$1 ');
  }
  return (neg ? '−' : '') + s;
}

function apply(a: bigint, b: bigint, op: Op): bigint | string {
  switch (op) {
    case '+':
      return a + b;
    case '−':
      return a - b;
    case '×':
      return a * b;
    case '÷':
      return b === BigInt(0) ? 'Division by zero' : a / b;
    case 'mod':
      return b === BigInt(0) ? 'Division by zero' : a % b;
    case 'AND':
      return a & b;
    case 'OR':
      return a | b;
    case 'XOR':
      return a ^ b;
    case '<<':
      return b < BigInt(0) || b > BigInt(4096) ? 'Shift must be 0-4096' : a << b;
    case '>>':
      return b < BigInt(0) || b > BigInt(4096) ? 'Shift must be 0-4096' : a >> b;
  }
}

function twos(v: bigint, bits: number): string | null {
  const min = -(BigInt(1) << BigInt(bits - 1));
  const max = (BigInt(1) << BigInt(bits - 1)) - BigInt(1);
  if (v < min || v > max) return null;
  const u = v < BigInt(0) ? (BigInt(1) << BigInt(bits)) + v : v;
  return u
    .toString(2)
    .padStart(bits, '0')
    .replace(/(.{4})(?!$)/g, '$1 ');
}

export default function BrowserBaseCalculator({
  config,
}: {
  config?: { toolSlug?: string; variant?: string; heading?: string };
}) {
  const base = (config?.variant === 'hex' ? 16 : config?.variant === 'octal' ? 8 : 2) as Base;
  const [a, setA] = useState(base === 16 ? 'FF' : base === 8 ? '17' : '1011');
  const [b, setB] = useState(base === 16 ? '1A' : base === 8 ? '5' : '110');
  const [op, setOp] = useState<Op>('+');
  const reported = useRef(false);
  const touch = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(
      config?.toolSlug ?? (base === 16 ? 'hex-calculator' : 'binary-calculator'),
      '',
    ).catch(() => undefined);
  };

  const out = useMemo(() => {
    const va = parseBase(a, base);
    const vb = parseBase(b, base);
    if (va === null) return { error: `First number is not valid ${NAMES[base].toLowerCase()}.` };
    if (vb === null) return { error: `Second number is not valid ${NAMES[base].toLowerCase()}.` };
    const r = apply(va, vb, op);
    if (typeof r === 'string') return { error: r };
    return { va, vb, r };
  }, [a, b, op, base]);

  const field = (label: string, value: string, set: (v: string) => void) => (
    <label className="tool-live-field tool-live-field-full">
      <span>{label}</span>
      <input
        autoCapitalize="characters"
        onChange={(e) => {
          touch();
          set(e.target.value);
        }}
        spellCheck={false}
        style={{ fontFamily: 'ui-monospace, monospace', fontSize: '1.1rem' }}
        type="text"
        value={value}
      />
    </label>
  );

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{NAMES[base]}</span>
        <h2 className="tool-live-title">{config?.heading ?? `${NAMES[base]} calculator`}</h2>
        <p className="tool-live-subtitle">
          Exact arithmetic on whole numbers of any length, with the answer in binary, octal, decimal
          and hex.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          {field(`First number (${NAMES[base].toLowerCase()})`, a, setA)}
          <div className="tool-live-actions" role="radiogroup" aria-label="Operation">
            {OPS.map((o) => (
              <button
                aria-pressed={op === o}
                className={op === o ? 'button' : 'button button-secondary'}
                key={o}
                onClick={() => {
                  touch();
                  setOp(o);
                }}
                style={{ minWidth: '3rem' }}
                type="button"
              >
                {o}
              </button>
            ))}
          </div>
          {field(
            op === '<<' || op === '>>'
              ? `Shift by (${NAMES[base].toLowerCase()})`
              : `Second number (${NAMES[base].toLowerCase()})`,
            b,
            setB,
          )}
        </section>
        <section className="tool-live-preview">
          {'error' in out ? (
            <p className="tool-live-panel-note">{out.error}</p>
          ) : (
            <>
              <div className="tool-html-meta-grid">
                {([base, ...([2, 16, 10, 8] as Base[]).filter((x) => x !== base)] as Base[]).map(
                  (x) => (
                    <div className="tool-live-field" key={x}>
                      <span>
                        Result in {NAMES[x].toLowerCase()}
                        {x === base ? ' ✓' : ''}
                      </span>
                      <input
                        readOnly
                        style={{ fontFamily: 'ui-monospace, monospace' }}
                        type="text"
                        value={
                          (out.r < BigInt(0) ? '−' : '') +
                          PREFIX[x] +
                          formatBase(out.r < BigInt(0) ? -out.r : out.r, x)
                        }
                      />
                    </div>
                  ),
                )}
              </div>
              <p className="tool-live-panel-note" style={{ fontVariantNumeric: 'tabular-nums' }}>
                In decimal: {out.va.toString()} {op} {out.vb.toString()} = {out.r.toString()}
                {op === '÷' ? ' (whole-number division; the remainder is what mod gives)' : ''}
              </p>
              {out.r < BigInt(0) ? (
                <p className="tool-live-panel-note">
                  Negative result. As a signed 8, 16 and 32-bit two’s-complement value:{' '}
                  {[8, 16, 32]
                    .map((bits) => {
                      const t = twos(out.r, bits);
                      return t ? `${bits}-bit ${t}` : `${bits}-bit out of range`;
                    })
                    .join('; ')}
                  .
                </p>
              ) : null}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
